// client/src/components/SquadTacticalView.jsx
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaExchangeAlt,
  FaArrowLeft,
  FaTimes,
  FaTshirt,
  FaStar,
  FaBolt,
  FaShieldAlt,
  FaFutbol,
  FaRunning,
  FaGripVertical,
  FaCheckCircle,
  FaCrown,
  FaArrowRight,
  FaExclamationTriangle,
  FaInfoCircle,
} from 'react-icons/fa';

// ========== دالة الحصول على صورة اللاعب (للدوائر) ==========
const getPlayerImage = (position, rarity) => {
  const map = { Goalkeeper: 'goalkeeper', Defender: 'defender', Midfielder: 'midfielder', Striker: 'striker' };
  const base = map[position] || 'player';
  if (rarity === 'gold' || rarity === 'silver' || rarity === 'bronze') return `/img/${rarity}_${base}.jpg`;
  return `/img/${base}.jpg`;
};

// ========== توزيع اللاعبين (من اليسار إلى اليمين) ==========
const getPositionsForSquad = (squad) => {
  const groups = { Goalkeeper: [], Defender: [], Midfielder: [], Striker: [] };
  squad.forEach(p => {
    if (groups[p.position]) groups[p.position].push(p);
  });

  const coords = [];
  const pitchHeight = 80;
  const margin = 10;

  if (groups.Goalkeeper.length) {
    coords.push({ player: groups.Goalkeeper[0], x: 8, y: 50 });
  }

  const distributeVertical = (players, xPos) => {
    const count = players.length;
    if (count === 0) return;
    if (count === 1) {
      coords.push({ player: players[0], x: xPos, y: 50 });
    } else {
      const step = (pitchHeight - 2 * margin) / (count - 1);
      players.forEach((p, i) => {
        const y = margin + i * step;
        coords.push({ player: p, x: xPos, y });
      });
    }
  };

  distributeVertical(groups.Defender, 28);
  distributeVertical(groups.Midfielder, 50);
  distributeVertical(groups.Striker, 78);

  return coords;
};

// ========== شريط إحصائية ==========
const StatBar = ({ label, value, icon: Icon, color }) => {
  const c = {
    red: { bar: 'from-red-500 to-red-600', bg: 'bg-red-950/50', text: 'text-red-400' },
    blue: { bar: 'from-blue-500 to-blue-600', bg: 'bg-blue-950/50', text: 'text-blue-400' },
    green: { bar: 'from-green-500 to-emerald-600', bg: 'bg-green-950/50', text: 'text-green-400' },
    purple: { bar: 'from-purple-500 to-violet-600', bg: 'bg-purple-950/50', text: 'text-purple-400' },
    orange: { bar: 'from-orange-500 to-amber-600', bg: 'bg-orange-950/50', text: 'text-orange-400' },
    cyan: { bar: 'from-cyan-500 to-teal-600', bg: 'bg-cyan-950/50', text: 'text-cyan-400' },
  }[color] || { bar: 'from-red-500 to-red-600', bg: 'bg-red-950/50', text: 'text-red-400' };
  const pct = Math.min(value || 0, 100);

  return (
    <div className={`${c.bg} rounded-lg p-2.5 border border-white/5`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className={`${c.text} text-[10px]`} />}
          <span className="text-[11px] text-gray-300 font-medium">{label}</span>
        </div>
        <span className="text-[12px] font-black text-white font-mono">{value || 0}</span>
      </div>
      <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          className={`h-full bg-gradient-to-r ${c.bar} rounded-full relative`}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full" />
        </motion.div>
      </div>
    </div>
  );
};

// ========== لوحة تفاصيل اللاعب ==========
const PlayerDetails = ({ player, onClose, isInSwap, onSetCaptain, t }) => {
  if (!player) return null;

  const rStyles = {
    gold: {
      badge: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black',
      glow: 'shadow-yellow-500/30',
      border: 'border-yellow-500/30',
      title: 'from-yellow-300 to-amber-400',
      imgBorder: 'border-yellow-400',
    },
    silver: {
      badge: 'bg-gradient-to-r from-gray-300 to-gray-400 text-black',
      glow: 'shadow-gray-400/30',
      border: 'border-gray-400/30',
      title: 'from-gray-200 to-gray-400',
      imgBorder: 'border-gray-300',
    },
    bronze: {
      badge: 'bg-gradient-to-r from-amber-600 to-amber-700 text-white',
      glow: 'shadow-amber-600/30',
      border: 'border-amber-600/30',
      title: 'from-amber-400 to-amber-600',
      imgBorder: 'border-amber-600',
    },
    common: {
      badge: 'bg-gradient-to-r from-gray-600 to-gray-700 text-white',
      glow: 'shadow-white/10',
      border: 'border-white/10',
      title: 'from-white to-gray-300',
      imgBorder: 'border-white/30',
    },
  };
  const r = rStyles[player.rarity] || rStyles.common;
  const overall = Math.round(
    ((player.attack || 50) + (player.defense || 50) + (player.speed || 50) + (player.accuracy || 50)) / 4
  );
  const posIcon = { Goalkeeper: FaShieldAlt, Defender: FaShieldAlt, Midfielder: FaFutbol, Striker: FaBolt };
  const PosIcon = posIcon[player.position] || FaTshirt;

  const rarityLabel = {
    gold: t ? t('player.gold') : 'Or',
    silver: t ? t('player.silver') : 'Argent',
    bronze: t ? t('player.bronze') : 'Bronze',
    common: t ? t('player.common') : 'Commun',
  };

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', stiffness: 280, damping: 30 }}
      className={`fixed right-0 top-0 h-full w-72 sm:w-80 md:w-[340px] bg-gradient-to-b from-gray-900 via-gray-900/98 to-gray-950 backdrop-blur-2xl border-l-2 ${r.border} shadow-2xl ${r.glow} z-[60] overflow-y-auto`}
    >
      <div className={`relative h-28 bg-gradient-to-br ${r.title} opacity-20`} />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-gray-300 hover:text-white transition z-10 border border-white/10"
      >
        <FaTimes size={14} />
      </button>
      <div className="relative -mt-16 px-5 pb-6">
        <div className="flex justify-center">
          <div
            className={`relative w-24 h-24 rounded-2xl overflow-hidden border-4 ${r.imgBorder} shadow-2xl`}
          >
            <img
              src={getPlayerImage(player.position, player.rarity)}
              alt={player.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 h-8 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
              <span className="text-lg font-black text-white">{overall}</span>
            </div>
          </div>
        </div>
        <div className="text-center mt-3">
          <h2 className="text-xl font-black text-white tracking-tight">{player.name}</h2>
          <div className="flex items-center justify-center gap-2 mt-1.5">
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${r.badge}`}
            >
              <PosIcon size={9} />
              {{ Goalkeeper: 'GK', Defender: 'DEF', Midfielder: 'MID', Striker: 'STR' }[player.position]}
            </span>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${r.badge}`}
            >
              {player.rarity === 'gold'
                ? '⭐ ' + rarityLabel.gold
                : player.rarity === 'silver'
                ? '🥈 ' + rarityLabel.silver
                : player.rarity === 'bronze'
                ? '🥉 ' + rarityLabel.bronze
                : rarityLabel.common}
            </span>
          </div>
          {player.is_captain && (
            <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 text-xs font-bold">
              👑 {t ? t('player.captain') : 'Capitaine de l\'équipe'}
            </div>
          )}
        </div>
        <div className="mt-5 space-y-2">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
            {t ? t('player.stats') : 'Statistiques'}
          </h3>
          <StatBar label={t ? t('statistics.attack') : 'Attaque'} value={player.attack} icon={FaBolt} color="red" />
          <StatBar label={t ? t('statistics.defense') : 'Défense'} value={player.defense} icon={FaShieldAlt} color="blue" />
          <StatBar label={t ? t('statistics.speed') : 'Vitesse'} value={player.speed} icon={FaRunning} color="green" />
          <StatBar label={t ? t('statistics.accuracy') : 'Précision'} value={player.accuracy} icon={FaFutbol} color="purple" />
          {player.strength !== undefined && (
            <StatBar label={t ? t('statistics.strength') : 'Force'} value={player.strength} icon={FaStar} color="orange" />
          )}
          {player.tactics !== undefined && (
            <StatBar label={t ? t('statistics.tactics') : 'Tactique'} value={player.tactics} icon={FaStar} color="cyan" />
          )}
        </div>
        {player.special_ability && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-4 bg-gradient-to-r from-purple-900/30 via-purple-800/20 to-violet-900/30 border border-purple-500/20 rounded-xl p-3"
          >
            <div className="text-[10px] text-purple-400 font-bold uppercase tracking-wider mb-1">
              {t ? t('player.specialAbility') : 'Capacité spéciale'}
            </div>
            <div className="text-sm text-purple-200 font-bold flex items-center gap-1.5">
              ✨ {player.special_ability}
            </div>
          </motion.div>
        )}
        {isInSwap && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center"
          >
            <p className="text-yellow-400 text-xs font-bold">🔄 {t ? t('player.selectedForSwap') : 'Sélectionné pour l\'échange'}</p>
          </motion.div>
        )}

        <div className="mt-5">
          {!player.is_captain ? (
            <button
              onClick={() => {
                onSetCaptain(player.id);
                onClose();
              }}
              className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold py-2.5 rounded-xl transition shadow-lg flex items-center justify-center gap-2 text-sm"
            >
              <FaStar /> {t ? t('squadManager.setCaptain') : 'Définir comme capitaine'}
            </button>
          ) : (
            <div className="w-full bg-yellow-500/20 border border-yellow-400/50 rounded-xl p-3 text-center text-yellow-300 flex items-center justify-center gap-2 text-sm">
              <FaStar /> {t ? t('squadManager.currentCaptain') : 'Capitaine actuel'}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ========== خطوط الملعب ==========
const PitchLines = () => (
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute inset-[3%] border-[1.5px] border-white/35 rounded-sm" />
    <div className="absolute left-1/2 top-[3%] bottom-[3%] w-[1.5px] bg-white/35" />
    <div className="absolute left-1/2 top-1/2 w-[14%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-white/35" />
    <div className="absolute left-1/2 top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
    <div className="absolute left-[3%] top-1/2 -translate-y-1/2 w-[14%] h-[40%] border-[1.5px] border-white/35 border-l-0 rounded-r-sm" />
    <div className="absolute right-[3%] top-1/2 -translate-y-1/2 w-[14%] h-[40%] border-[1.5px] border-white/35 border-r-0 rounded-l-sm" />
    <div className="absolute left-[3%] top-1/2 -translate-y-1/2 w-[6%] h-[22%] border-[1.5px] border-white/35 border-l-0 rounded-r-sm" />
    <div className="absolute right-[3%] top-1/2 -translate-y-1/2 w-[6%] h-[22%] border-[1.5px] border-white/35 border-r-0 rounded-l-sm" />
    <div className="absolute left-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-r-[1.5px] border-white/35 rounded-tr-full" />
    <div className="absolute left-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-r-[1.5px] border-white/35 rounded-br-full" />
    <div className="absolute right-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-l-[1.5px] border-white/35 rounded-tl-full" />
    <div className="absolute right-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-l-[1.5px] border-white/35 rounded-bl-full" />
    <div className="absolute left-[17%] top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40" />
    <div className="absolute right-[17%] top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40" />
  </div>
);

// ========== مكون الدائرة (اللاعب) ==========
const PlayerCircle = ({ player, isDragging, isDragOver, isSelectedOut, onClick, isRTL }) => {
  const getPlayerPower = (p) => {
    if (!p) return 0;
    const attack = Number(p.attack) || 0;
    const defense = Number(p.defense) || 0;
    const speed = Number(p.speed) || 0;
    const accuracy = Number(p.accuracy) || 0;
    const strength = Number(p.strength) || 50;
    const tactics = Number(p.tactics) || 50;
    const { position, special_ability } = p;
    let power = 0;
    if (position === 'Striker') {
      power = (speed * 0.4 + accuracy * 0.4 + attack * 0.3 + defense * 0.2 + strength * 0.4 + tactics * 0.3) / 2.0;
    } else if (position === 'Defender') {
      power = (defense * 0.4 + speed * 0.3 + strength * 0.5 + tactics * 0.3 + attack * 0.2 + accuracy * 0.2) / 1.9;
    } else if (position === 'Midfielder') {
      power = (speed * 0.3 + defense * 0.3 + attack * 0.3 + accuracy * 0.3 + strength * 0.4 + tactics * 0.5) / 2.1;
    } else if (position === 'Goalkeeper') {
      power = (attack + defense + speed + accuracy + strength + tactics) / 6;
    } else {
      power = (attack + defense + speed + accuracy + strength + tactics) / 6;
    }
    if (special_ability === 'Playmaker') power *= 1.05;
    else if (special_ability === 'Super Scorer' && position === 'Striker') power *= 1.15;
    else if (special_ability === 'Super Saver' && position === 'Goalkeeper') power *= 1.15;
    else if (special_ability === 'Speed Runner') power *= 1.10;
    return Math.min(100, Math.max(0, power));
  };

  const power = Math.round(getPlayerPower(player));
  const isGoalkeeper = player.position === 'Goalkeeper';

  return (
    <div
      onClick={() => onClick(player)}
      className={`relative flex flex-col items-center transition-all duration-200 cursor-pointer ${
        isDragging ? 'opacity-30 scale-90' : ''
      } ${isDragOver ? 'ring-4 ring-yellow-400 ring-offset-2 ring-offset-black/50 scale-110' : ''} ${
        isSelectedOut ? 'ring-4 ring-red-500 ring-offset-2 ring-offset-black/50' : ''
      }`}
    >
      <div
        className={`relative w-14 h-14 rounded-full border-2 shadow-lg overflow-hidden ${
          isGoalkeeper ? 'border-yellow-400 shadow-yellow-400/30' : 'border-white/30'
        } ${isDragOver ? 'border-yellow-400' : ''}`}
        style={{
          background: isGoalkeeper
            ? 'linear-gradient(135deg, #f1c40f, #f39c12)'
            : 'linear-gradient(135deg, #e74c3c, #c0392b)',
        }}
      >
        <img
          src={getPlayerImage(player.position, player.rarity)}
          alt={player.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.onerror = null; e.target.src = '/img/player.jpg'; }}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] font-black text-center py-0.5">
          {power}
        </div>
        {player.is_captain && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-md border border-white/50">
            <span className="text-[8px] font-black text-black">C</span>
          </div>
        )}
      </div>
      <span className="text-[10px] font-bold text-white mt-1 truncate max-w-[60px] text-center">
        {player.name.length > 8 ? player.name.slice(0,6)+'..' : player.name}
      </span>
    </div>
  );
};

// ========== بطاقة البديل (دائرة مصغرة) ==========
const BenchCircle = ({
  player,
  index,
  isDragOver,
  isDragging,
  onClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDragLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  t,
  isRTL,
}) => {
  const getPlayerPower = (p) => {
    if (!p) return 0;
    const attack = Number(p.attack) || 0;
    const defense = Number(p.defense) || 0;
    const speed = Number(p.speed) || 0;
    const accuracy = Number(p.accuracy) || 0;
    const strength = Number(p.strength) || 50;
    const tactics = Number(p.tactics) || 50;
    const { position, special_ability } = p;
    let power = 0;
    if (position === 'Striker') {
      power = (speed * 0.4 + accuracy * 0.4 + attack * 0.3 + defense * 0.2 + strength * 0.4 + tactics * 0.3) / 2.0;
    } else if (position === 'Defender') {
      power = (defense * 0.4 + speed * 0.3 + strength * 0.5 + tactics * 0.3 + attack * 0.2 + accuracy * 0.2) / 1.9;
    } else if (position === 'Midfielder') {
      power = (speed * 0.3 + defense * 0.3 + attack * 0.3 + accuracy * 0.3 + strength * 0.4 + tactics * 0.5) / 2.1;
    } else if (position === 'Goalkeeper') {
      power = (attack + defense + speed + accuracy + strength + tactics) / 6;
    } else {
      power = (attack + defense + speed + accuracy + strength + tactics) / 6;
    }
    if (special_ability === 'Playmaker') power *= 1.05;
    else if (special_ability === 'Super Scorer' && position === 'Striker') power *= 1.15;
    else if (special_ability === 'Super Saver' && position === 'Goalkeeper') power *= 1.15;
    else if (special_ability === 'Speed Runner') power *= 1.10;
    return Math.min(100, Math.max(0, power));
  };

  const power = Math.round(getPlayerPower(player));

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: isDragging ? 0.3 : 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      onClick={() => onClick(player)}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-drop-type="bench"
      data-drop-index={index}
      className={`
        relative flex items-center gap-2.5 p-2 rounded-xl border cursor-grab active:cursor-grabbing transition-all duration-200
        ${isDragOver ? 'border-yellow-400 bg-yellow-400/20 shadow-lg shadow-yellow-500/25 scale-[1.04]' : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'}
      `}
      whileHover={!isDragging ? { scale: 1.02 } : {}}
      whileTap={{ scale: 0.98 }}
    >
      {isDragOver && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="absolute inset-0 rounded-xl border-2 border-yellow-400 pointer-events-none z-10"
        />
      )}

      <div className={`absolute -top-1.5 ${isRTL ? '-right-1.5' : '-left-1.5'} w-5 h-5 rounded-full bg-gray-800 border border-white/20 flex items-center justify-center text-[8px] font-bold text-gray-400 z-10`}>
        {index + 1}
      </div>

      <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/15 shadow-md flex-shrink-0">
        <img
          src={getPlayerImage(player.position, player.rarity)}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { e.target.onerror = null; e.target.src = '/img/player.jpg'; }}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-white truncate">{player.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span
            className={`text-[9px] font-bold px-1.5 py-px rounded ${
              player.rarity === 'gold'
                ? 'bg-yellow-500/20 text-yellow-300'
                : player.rarity === 'silver'
                ? 'bg-gray-400/20 text-gray-300'
                : player.rarity === 'bronze'
                ? 'bg-amber-600/20 text-amber-300'
                : 'bg-white/10 text-gray-400'
            }`}
          >
            {{ Goalkeeper: 'GK', Defender: 'DEF', Midfielder: 'MID', Striker: 'STR' }[player.position]}
          </span>
          <span className="text-[10px] font-black text-white">{power}</span>
        </div>
      </div>

      <FaGripVertical className="text-gray-600 text-xs flex-shrink-0" />

      {isDragOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg flex-shrink-0"
        >
          <FaExchangeAlt className="text-black text-[10px]" />
        </motion.div>
      )}
    </motion.div>
  );
};

// ========== إنشاء عنصر السحب المرئي ==========
const createDragGhost = (player, x, y, t) => {
  const imgSrc = getPlayerImage(player.position, player.rarity);

  const ghost = document.createElement('div');
  ghost.id = 'drag-ghost';
  ghost.style.cssText = `
    position: fixed; z-index: 99999; pointer-events: none; opacity: 0.92;
    transform: scale(1.05); display: flex; flex-direction: column; align-items: center;
    gap: 4px; padding: 8px 14px; background: rgba(15, 23, 42, 0.95);
    border-radius: 14px; border: 2px solid rgba(234, 179, 8, 0.6);
    box-shadow: 0 15px 40px rgba(0,0,0,0.6), 0 0 20px rgba(234,179,8,0.2);
    backdrop-filter: blur(10px);
  `;
  ghost.innerHTML = `
    <img src="${imgSrc}" style="width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid rgba(255,255,255,0.3);" onerror="this.src='/img/player.jpg'" />
    <span style="font-size:11px;font-weight:800;color:white;white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis;">${player.name}</span>
    <span style="font-size:9px;color:rgba(250,204,21,0.9);font-weight:700;">${t ? t('player.dragToSwap') : 'Glisser pour échanger'}</span>
  `;
  ghost.style.left = x - 40 + 'px';
  ghost.style.top = y - 40 + 'px';
  document.body.appendChild(ghost);
  return ghost;
};

// ===== مكون Toast احترافي =====
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-emerald-600 to-emerald-700',
          border: 'border-emerald-400/30',
          icon: <FaCheckCircle className="text-2xl text-emerald-200 flex-shrink-0" />,
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-600 to-red-700',
          border: 'border-red-400/30',
          icon: <FaExclamationTriangle className="text-2xl text-red-200 flex-shrink-0" />,
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-600 to-blue-700',
          border: 'border-blue-400/30',
          icon: <FaInfoCircle className="text-2xl text-blue-200 flex-shrink-0" />,
        };
    }
  };

  const styles = getStyles();

  return (
    <motion.div
      initial={{ opacity: 0, y: -60, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -60, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] px-6 py-3 rounded-2xl shadow-2xl border ${styles.bg} ${styles.border} flex items-center gap-3 text-sm font-bold backdrop-blur-sm max-w-[90vw]`}
    >
      {styles.icon}
      <span className="text-white text-center">{message}</span>
    </motion.div>
  );
};

// ========== المكون الرئيسي ==========
const SquadTacticalView = ({
  activeSquad,
  benchSquad,
  onSwap,
  onBack,
  onSave,
  onSetCaptain,
  saving = false,
  error = '',
  successMessage = '',
  teamStrength = 0,
  strengthWithoutCaptain = 0,
  captainBonus = 0,
  captain = null,
  t,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  // ========== حالة Toast ==========
  const [toast, setToast] = useState(null);

  // ========== دالة عرض Toast ==========
  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    setToast({ message, type });
    const timer = setTimeout(() => setToast(null), duration);
    return () => clearTimeout(timer);
  }, []);

  // ========== عرض رسائل النجاح / الخطأ من الأب ==========
  useEffect(() => {
    if (successMessage) {
      showToast(successMessage, 'success', 3000);
    }
  }, [successMessage, showToast]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error', 4000);
    }
  }, [error, showToast]);

  // ========== كشف حجم الشاشة ==========
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [dragSource, setDragSource] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [touchSource, setTouchSource] = useState(null);
  const touchGhostRef = useRef(null);
  const touchDropRef = useRef(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [swapping, setSwapping] = useState(false);

  // ========== مرجع للعنصر الذي يتم سحبه باللمس ==========
  const touchElementRef = useRef(null);

  const positionedPlayers = useMemo(() => getPositionsForSquad(activeSquad), [activeSquad]);

  // ======== التحقق من صحة التبديل (حارس مع حارس) ========
  const validateSwap = useCallback((activeIdx, benchIdx) => {
    const activePlayer = activeSquad[activeIdx];
    const benchPlayer = benchSquad[benchIdx];

    if (!activePlayer || !benchPlayer) {
      showToast(t ? t('squadManager.invalidSwap') : 'لا يمكن إجراء هذا التبديل', 'error', 4000);
      return false;
    }

    const isActiveGK = activePlayer.position === 'Goalkeeper';
    const isBenchGK = benchPlayer.position === 'Goalkeeper';

    if (isActiveGK && !isBenchGK) {
      showToast(t ? t('squadManager.goalkeeperSwapError') : 'لا يمكن استبدال حارس المرمى إلا بحارس مرمى آخر', 'error', 4000);
      return false;
    }

    if (!isActiveGK && isBenchGK) {
      showToast(t ? t('squadManager.outfieldSwapError') : 'لا يمكن استبدال لاعب عادي بحارس مرمى', 'error', 4000);
      return false;
    }

    return true;
  }, [activeSquad, benchSquad, t, showToast]);

  // ======== تنفيذ التبديل (مرفوع للأعلى ليتم تعريفه قبل استخدامه) ========
const performSwap = useCallback(
  (source, target) => {
    const activeIdx = source.type === 'active' ? source.index : target.index;
    const benchIdx = source.type === 'bench' ? source.index : target.index;

    if (!validateSwap(activeIdx, benchIdx)) {
      setDragSource(null);
      setDropTarget(null);
      setTouchSource(null);
      return;
    }

    setSwapping(true);
    setDragSource(null);
    setDropTarget(null);

    setTimeout(() => {
      onSwap(activeIdx, benchIdx);
      setSwapping(false);
      // ✅ تم إزالة showToast هنا، سيظهر فقط Toast حفظ التشكيلة
    }, 500);
  },
  [onSwap, validateSwap] // ✅ إزالة showToast من التبعيات
);

  // ======== HTML5 Drag & Drop ========
  const handleDragStart = useCallback(
    (e, type, index) => {
      setDragSource({ type, index });
      setDropTarget(null);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify({ type, index }));
      setTimeout(() => {
        if (e.target) e.target.style.opacity = '0.3';
      }, 0);
    },
    []
  );

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    setDragSource(null);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback(
    (e, type, index) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragSource) {
        if (dragSource.type !== type && dragSource.index !== index) {
          setDropTarget({ type, index });
        } else {
          setDropTarget(null);
        }
      }
    },
    [dragSource]
  );

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback(
    (e, type, index) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dragSource || dragSource.type === type) {
        setDragSource(null);
        setDropTarget(null);
        return;
      }
      performSwap(dragSource, { type, index });
    },
    [dragSource, performSwap]
  );

  // ======== Touch Drag & Drop ========
  const handleTouchStart = useCallback(
    (e, type, index) => {
      const touch = e.touches[0];
      const player = type === 'active' ? activeSquad[index] : benchSquad[index];
      if (!player) return;

      // حفظ مرجع العنصر الذي يتم سحبه
      touchElementRef.current = e.currentTarget;

      // منع التمرير أثناء السحب عن طريق تعيين touch-action: none
      if (touchElementRef.current) {
        touchElementRef.current.style.touchAction = 'none';
      }

      const ghost = createDragGhost(player, touch.clientX, touch.clientY, t);
      touchGhostRef.current = ghost;
      setTouchSource({ type, index });
      setDropTarget(null);
    },
    [activeSquad, benchSquad, t]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!touchSource || !touchGhostRef.current) return;
      // لا نستخدم preventDefault هنا لتجنب الخطأ في passive event listeners
      // نعتمد على touch-action: none لمنع التمرير

      const touch = e.touches[0];
      const ghost = touchGhostRef.current;
      ghost.style.left = touch.clientX - 40 + 'px';
      ghost.style.top = touch.clientY - 40 + 'px';

      ghost.style.display = 'none';
      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      ghost.style.display = '';

      const dropElem = elem?.closest('[data-drop-type]');
      if (dropElem) {
        const dType = dropElem.dataset.dropType;
        const dIndex = parseInt(dropElem.dataset.dropIndex, 10);
        if (dType !== touchSource.type) {
          if (
            !touchDropRef.current ||
            touchDropRef.current.type !== dType ||
            touchDropRef.current.index !== dIndex
          ) {
            touchDropRef.current = { type: dType, index: dIndex };
            setDropTarget({ type: dType, index: dIndex });
          }
        } else {
          if (touchDropRef.current) {
            touchDropRef.current = null;
            setDropTarget(null);
          }
        }
      } else {
        if (touchDropRef.current) {
          touchDropRef.current = null;
          setDropTarget(null);
        }
      }
    },
    [touchSource]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      if (!touchSource) return;

      // إعادة تعيين touch-action
      if (touchElementRef.current) {
        touchElementRef.current.style.touchAction = 'auto';
        touchElementRef.current = null;
      }

      if (touchGhostRef.current) {
        touchGhostRef.current.remove();
        touchGhostRef.current = null;
      }
      if (touchDropRef.current && touchDropRef.current.type !== touchSource.type) {
        performSwap(touchSource, touchDropRef.current);
      }
      setTouchSource(null);
      touchDropRef.current = null;
      setDropTarget(null);
    },
    [touchSource, performSwap]
  );

  const isSource = useCallback(
    (type, index) => {
      if (dragSource && dragSource.type === type && dragSource.index === index) return true;
      if (touchSource && touchSource.type === type && touchSource.index === index) return true;
      return false;
    },
    [dragSource, touchSource]
  );

  const isTarget = useCallback(
    (type, index) => {
      if (dropTarget && dropTarget.type === type && dropTarget.index === index) return true;
      return false;
    },
    [dropTarget]
  );

  const isDraggingAny = dragSource !== null || touchSource !== null;

  // ========== RTL/LTR adjustments ==========
  const backIcon = isRTL ? <FaArrowRight /> : <FaArrowLeft />;
  const flexDir = isRTL ? 'flex-row-reverse' : 'flex-row';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0d1f35] to-[#0a1628] text-white relative overflow-hidden select-none" dir={i18n.dir()}>
      {/* ===== Toast ===== */}
      <AnimatePresence>
        {toast && (
          <ToastNotification
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">

        {/* ===== الهيدر ===== */}
        <div className={`flex items-center justify-between gap-3 mb-5 ${flexDir}`}>
          <button
            onClick={onBack}
            className={`flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 px-3 py-2 rounded-xl text-sm transition-all flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {backIcon}
            <span className="hidden sm:inline">{t ? t('common.back') : 'Retour'}</span>
          </button>

          <div className="text-center flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-black bg-gradient-to-r from-emerald-300 via-cyan-300 to-emerald-400 bg-clip-text text-transparent truncate">
              {t ? t('squadManager.tacticalView') : 'Gestion de l\'équipe'}
            </h1>
            <div className="flex items-center justify-center gap-2 mt-0.5 text-[10px] text-gray-400">
              <span>{t ? t('squadManager.active') : 'Titulaires'}: {activeSquad.length}/6</span>
              <span className="text-gray-600">•</span>
              <span>{t ? t('squadManager.bench') : 'Remplaçants'}: {benchSquad.length}/4</span>
            </div>
          </div>

          <button
            onClick={onSave}
            disabled={saving}
            className={`bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold px-4 py-2 rounded-xl text-sm transition disabled:opacity-50 shadow-lg flex items-center gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden sm:inline">{t ? t('common.saving') : 'Enregistrement...'}</span>
              </>
            ) : (
              <>
                <FaCheckCircle className="text-sm" />
                <span className="whitespace-nowrap">{t ? t('common.save') : 'Enregistrer'}</span>
              </>
            )}
          </button>
        </div>

        {/* ===== عرض تأثير الكابتن ===== */}
        {captain ? (
          <div className="mb-5 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-xl p-3 shadow-lg flex flex-wrap items-center justify-between gap-2">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <FaCrown className="text-yellow-400 text-2xl" />
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <div className="text-sm font-bold text-white flex items-center gap-2">
                  {t('squadManager.captain')}: <span className="text-yellow-300">{captain.name}</span>
                  {captain.special_ability && (
                    <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full">
                      {captain.special_ability}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {t('squadManager.strengthWithoutCaptain')}: {strengthWithoutCaptain}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-[10px] text-gray-400">{t('squadManager.captainBonus')}</div>
                <div className="text-base font-bold text-green-400">+{captainBonus}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] text-gray-400">{t('squadManager.totalStrength')}</div>
                <div className="text-lg font-bold text-yellow-300">{teamStrength}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-5 bg-gray-800/30 border border-white/10 rounded-xl p-3 text-center text-gray-400 text-sm">
            {t('squadManager.noCaptain') || 'لم يتم تعيين كابتن. اختر لاعباً واضغط "تعيين كابتن"'}
          </div>
        )}

        {/* ===== تلميح السحب ===== */}
        {isDraggingAny && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2.5 mb-4 flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ repeat: Infinity, duration: 0.6 }}
              className={isRTL ? 'ml-2' : 'mr-2'}
            >
              <FaExchangeAlt className="text-cyan-400 text-sm" />
            </motion.div>
            <span className="text-xs text-cyan-300 font-medium">
              {t ? t('player.dragHint') : 'Glissez un joueur du terrain vers le banc ou vice-versa'}
            </span>
          </motion.div>
        )}

        {/* ===== الملعب ===== */}
        <div className="relative w-full mx-auto" style={{ aspectRatio: '16/9' }}>
          <div className="relative w-full h-full" style={{ perspective: '1200px' }}>
            <div
              className="relative w-full h-full transition-transform duration-700"
              style={{ transform: 'rotateX(8deg)' }}
            >
              <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border-2 border-white/15">
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-700 via-green-700 to-emerald-800" />
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute w-full ${i % 2 === 0 ? 'bg-emerald-600/25' : 'bg-green-800/25'}`}
                    style={{ top: `${i * 10}%`, height: '10%' }}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/20" />

                {isDraggingAny && (
                  <div className="absolute inset-0 z-[5]">
                    {positionedPlayers.map((item) => {
                      const idx = activeSquad.indexOf(item.player);
                      if (isSource('active', idx)) return null;
                      const isTgt = isTarget('active', idx);
                      return (
                        <div
                          key={`drop-${item.player.id}`}
                          data-drop-type="active"
                          data-drop-index={idx}
                          className="absolute"
                          style={{
                            left: `${item.x}%`,
                            top: `${item.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: isMobile ? '60px' : '80px',
                            height: isMobile ? '80px' : '110px',
                            borderRadius: '16px',
                            border: isTgt
                              ? '2px dashed rgba(234,179,8,0.8)'
                              : '2px dashed rgba(255,255,255,0.1)',
                            background: isTgt ? 'rgba(234,179,8,0.1)' : 'transparent',
                            transition: 'all 0.2s',
                          }}
                        />
                      );
                    })}
                  </div>
                )}

                <PitchLines />

                {positionedPlayers.map((item, i) => {
                  const player = item.player;
                  const idx = activeSquad.indexOf(player);
                  const dragging = isSource('active', idx);
                  const over = isTarget('active', idx);

                  return (
                    <motion.div
                      key={player.id}
                      className="absolute"
                      style={{
                        left: `${item.x}%`,
                        top: `${item.y}%`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: dragging ? 1 : over ? 35 : 10,
                      }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{
                        scale: swapping && dragging ? [1, 0, 0] : over ? 1.15 : 1,
                        opacity: swapping && dragging ? [1, 0, 0] : 1,
                      }}
                      transition={{
                        delay: idx * 0.06,
                        type: 'spring',
                        stiffness: 350,
                        damping: 25,
                      }}
                    >
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-10 h-2 bg-black/25 rounded-full blur-sm" />

                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, 'active', idx)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, 'active', idx)}
                        onDrop={(e) => handleDrop(e, 'active', idx)}
                        onDragLeave={handleDragLeave}
                        onTouchStart={(e) => handleTouchStart(e, 'active', idx)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        data-drop-type="active"
                        data-drop-index={idx}
                        style={{ transform: 'rotateX(-18deg)', transformOrigin: 'center bottom' }}
                      >
                        <PlayerCircle
                          player={player}
                          isDragging={dragging}
                          isDragOver={over}
                          isSelectedOut={false}
                          onClick={setSelectedPlayer}
                          isRTL={isRTL}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ===== دكة البدلاء ===== */}
        <div className="mt-4">
          <div className={`flex items-center justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-7 h-7 rounded-lg bg-yellow-500/15 border border-yellow-500/25 flex items-center justify-center">
                <FaTshirt className="text-yellow-400 text-xs" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">
                  {t ? t('squadManager.bench') : 'Remplaçants'}
                </h3>
              </div>
            </div>
            <div className={`flex items-center gap-1.5 text-[10px] text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <FaGripVertical size={8} />
              <span>{t ? t('player.dragToSwap') : 'Glisser pour échanger'}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {benchSquad.map((player, idx) => (
              <BenchCircle
                key={player.id}
                player={player}
                index={idx}
                isDragOver={isTarget('bench', idx)}
                isDragging={isSource('bench', idx)}
                onClick={setSelectedPlayer}
                onDragStart={(e) => handleDragStart(e, 'bench', idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, 'bench', idx)}
                onDrop={(e) => handleDrop(e, 'bench', idx)}
                onDragLeave={handleDragLeave}
                onTouchStart={(e) => handleTouchStart(e, 'bench', idx)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                t={t}
                isRTL={isRTL}
              />
            ))}

            {[...Array(Math.max(0, 4 - benchSquad.length))].map((_, i) => (
              <div
                key={`empty-${i}`}
                className="flex items-center justify-center p-4 rounded-xl border border-dashed border-white/10 bg-white/[0.01]"
              >
                <span className="text-[10px] text-gray-600">
                  {t ? t('player.empty') : 'Vide'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ===== معلومة إضافية ===== */}
        <div className="mt-6 text-center">
          <div className={`inline-flex items-center gap-3 bg-white/[0.03] border border-white/8 rounded-xl px-6 py-3 text-gray-500 text-[11px] ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span>👆 {t ? t('player.tapHint') : 'Appuyez sur un joueur pour voir ses détails'}</span>
          </div>
        </div>
      </div>

      {/* ===== لوحة التفاصيل ===== */}
      <AnimatePresence>
        {selectedPlayer && !isDraggingAny && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedPlayer(null)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[55]"
            />
            <PlayerDetails
              player={selectedPlayer}
              onClose={() => setSelectedPlayer(null)}
              isInSwap={false}
              onSetCaptain={onSetCaptain}
              t={t}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SquadTacticalView;